import React, { createContext, useContext, useEffect, useState } from "react";
import { Buffer } from "buffer";
import { useNavigate } from "react-router-dom";
import {
  clearRefreshTokenPromise,
  refreshTokenPromise,
  triggerRefreshToken,
} from "utils/api";

/**
 * @typedef AuthContextType
 * @property {Object} user = parsed user object from the jwt token or stored session
 * @property {string} token - the user's token
 * @property {(responseData: { token: string }) => void} login - invoke to log the user in
 * @property {(type: "TOKEN_EXPIRED" | "LOGOUT") => void} logout - invoke to log the user out
 * @property {(token: string) => void} updateToken - invoke to update the token
 * @property {() => void} refreshToken - invoke to refresh the token, logs the user out if refresh token fails
 */

/** @type {import('react').Context<AuthContextType | undefined>} */
const AuthContext = createContext(null);

//populate user state with session on initial load
const defaultUser = { uiPermissions: [] };
let initialUser;
let initialToken;
try {
  const initialSession = JSON.parse(localStorage.getItem("session")) ?? null;
  initialUser = initialSession?.isoAgent ?? defaultUser;
  initialToken = initialSession?.token ?? null;
} catch (error) {
  initialUser = defaultUser;
  initialToken = null;
}

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [token, setToken] = useState(initialToken);

  useEffect(() => {
    //custom event to update token for Axios interceptors
    const callUpdateToken = ({ detail: token }) => updateToken(token);
    window.addEventListener("updateTokenEvent", callUpdateToken);
    return () =>
      window.removeEventListener("updateTokenEvent", callUpdateToken);
  }, []);

  const refreshToken = async () => {
    if (!user.exp) {
      logout();
      return;
    }
    const canBeRefreshed = user.refreshTokenExp > Date.now() / 1000;
    // console.log("canBeRefreshed", canBeRefreshed);
    if (!canBeRefreshed) {
      logout("TOKEN_EXPIRED");
    } else if (!refreshTokenPromise) {
      triggerRefreshToken();
      const tokenResponse = await refreshTokenPromise;
      if (tokenResponse.responseCode === "0000") {
        //Only update token if refresh token is successful
        updateToken(tokenResponse.data.token);
      } else {
        //If refresh token fails, logout
        logout("TOKEN_EXPIRED");
      }
      clearRefreshTokenPromise();
    }
  };

  const updateToken = (token) => {
    const isoAgent = parseJwtToken(token);

    setUser({
      ...isoAgent,
    });
    setToken(token);

    const sessionString = JSON.stringify({ isoAgent, token });
    // console.log("sessionString= " + sessionString);
    localStorage.setItem("session", sessionString);
    sessionStorage.setItem("session", sessionString);

    localStorage.setItem(
      "account-name",
      JSON.parse(JSON.stringify(isoAgent.firstName)),
    );
  };

  const login = (responseData) => {
    updateToken(responseData.token);
    return navigate("/master");
  };

  const logout = (type) => {
    setUser(defaultUser);
    setToken(null);
    localStorage.clear();
    sessionStorage.clear();

    switch (type) {
      case "TOKEN_EXPIRED":
        return navigate("/user/token-expired", { replace: true });
      case "LOGOUT":
      default:
        return navigate("/user/login", { replace: true });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        updateToken,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const parseJwtToken = (token) => {
  if (token) {
    const jwtPayload = token.split(".");
    if (jwtPayload.length == 3) {
      try {
        const jwtdecoded = Buffer.from(jwtPayload[1], "base64");
        const jwtjson = JSON.parse(jwtdecoded);
        // console.log("jwtjson parsed:" + jwtdecoded);
        return jwtjson;
      } catch (e) {
        console.log("jwt parse exception= " + e);
      }
    } else {
      console.log("malformed jwt token");
    }
  }
  return {};
};

export const testJwtDecode = (token) => {
  return parseJwtToken(token);
};

export const useAuth = () => {
  return useContext(AuthContext);
};
